import { useEffect, useState } from 'react';
import { useCreatePushTokenMutation } from '@hook/message/useApi';
import { EApiPlatfrom } from '@datatype/message/api-type';
import { IS_IOS, IS_ANDROID, IS_REACT_NATIVE_APP } from '@config/environment';
import {
  WEB_TO_APP,
  APP_TO_WEB,
  sendApplicationEventByBridge,
  receiveApplicationEventByBridge,
} from '@constant/common/bridge-event';
import { VERSION_KEY } from '@constant/version';

interface IPushTokenPayload {
  deviceId: string;
  token: string | null;
}

/**
 * 앱(React Native)에서 전달받은 Push Token을 수신하는 Hook
 * - 앱에서 CustomEvent('pushToken')로 토큰 + deviceId 전달
 * - 수신 후 서버에 저장
 */
export const usePushMessage = () => {
  const mutation = useCreatePushTokenMutation();

  useEffect(() => {
    if (!IS_REACT_NATIVE_APP) return;

    (async () => {
      try {
        const { token, deviceId } = await receiveApplicationEventByBridge<IPushTokenPayload>(APP_TO_WEB.PUSH_TOKEN);
        console.log(`=== Push Token Received from App: deviceId(${deviceId}) & token(${token}) ===`);

        const platform = IS_IOS ? EApiPlatfrom.IOS : IS_ANDROID ? EApiPlatfrom.ANDROID : null;
        if (!platform) return;
        mutation.mutate({ platform, service: 'IYAGIGO', deviceId, token });
      } catch {
        // 리로드 시 RN이 토큰을 재전송하지 않아 timeout 발생 — 무시
      }
    })();
  }, []);
};

interface IApplicationMetadataPayload {
  platform: string;
  version: string;
  model: string;
}

interface IApplicationMetadataResult extends IApplicationMetadataPayload {
  // 요청·재시도가 모두 끝났는지. false면 아직 응답 대기 중이므로 version('-')으로 판단하면 안 된다.
  isChecked: boolean;
}

// 메타데이터 요청 1회당 응답 대기 시간
// 앱의 전송은 네트워크가 아니라 injectJavaScript라 지연이 거의 없다. 느린 기기를 감안해 1.5초.
const APPLICATION_METADATA_TIMEOUT = 1500;
// 무응답 시 재시도 횟수 (앱 1.0.21 미만은 요청을 처리하지 못한다)
const APPLICATION_METADATA_RETRY = 1;

// 아직 응답을 받지 못한 상태의 기본값
const INITIAL_APPLICATION_METADATA: IApplicationMetadataPayload = {
  platform: '-',
  version: '-',
  model: '-',
};

/**
 * 앱에 메타데이터(CHECK_APPLICATION_METADATA)를 요청해 platform/version/model을 반환하는 Hook
 * - 마운트 시 요청하고, 응답이 오면 state가 갱신되며 리렌더된다.
 * - 응답 전이거나 무응답이면 '-'를 유지한다. 둘은 isChecked로 구분한다.
 *   (isChecked === false → 대기 중 / isChecked === true && version === '-' → 무응답 확정)
 * - 무응답은 구버전 앱과 일시적 브리지 실패를 구분할 수 없으므로 강제 안내에 쓰지 않는다.
 */
export const useCheckApplicationMetadata = (): IApplicationMetadataResult => {
  const [metadata, setMetadata] = useState<IApplicationMetadataPayload>(INITIAL_APPLICATION_METADATA);
  // 재시도까지 모두 끝났는지. 응답 대기 중('-')과 무응답 확정을 구분하기 위해 필요하다.
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    if (IS_REACT_NATIVE_APP) {
      (async () => {
        for (let attempt = 0; attempt <= APPLICATION_METADATA_RETRY; attempt++) {
          try {
            // listener를 먼저 등록해야 앱의 즉시 응답을 놓치지 않는다
            const receiving = receiveApplicationEventByBridge<IApplicationMetadataPayload>(
              APP_TO_WEB.APPLICATION_METADATA,
              APPLICATION_METADATA_TIMEOUT,
            );
            sendApplicationEventByBridge(WEB_TO_APP.CHECK_APPLICATION_METADATA);

            const received = await receiving;
            if (isCancelled) return;

            console.log(
              `=== App Info Received: platform(${received.platform}) & version(${received.version}) & model(${received.model}) ===`,
            );
            setMetadata(received);
            setIsChecked(true);
            return;
          } catch {
            // timeout — 앱 1.0.21 미만이거나 일시적인 브리지 실패
            if (isCancelled) return;
          }
        }
        console.warn('=== App Info not received: 구버전 앱이거나 브리지 응답 없음 ===');
        setIsChecked(true);
      })();
    } else {
      setMetadata({ platform: 'web', version: localStorage.getItem(VERSION_KEY) ?? '-', model: '-' });
      setIsChecked(true);
    }

    return () => {
      isCancelled = true;
    };
  }, []);

  return { ...metadata, isChecked };
};

/**
 * WebView 준비 상태를 앱에 알리는 Hook
 * - 웹 로드 완료 시 앱에 WEBVIEW_READY 전송
 */
export const useWebviewStatus = () => {
  useEffect(() => {
    sendApplicationEventByBridge(WEB_TO_APP.WEBVIEW_READY);
  }, []);
};
